import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, CheckCircle, XCircle, Mail, Lock, Key, User, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

import { BACKEND_URL, API } from '../config';
const o = { withCredentials: true };

const Settings = () => {
  const { user, checkAuth } = useAuth();
  const [tab, setTab] = useState('account');
  const [exchanges, setExchanges] = useState([]);
  const [supportedExchanges, setSupportedExchanges] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    exchange_id: '', api_key: '', api_secret: '', api_passphrase: '', testnet: false
  });

  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [exchangesRes, supportedRes] = await Promise.all([
        axios.get(`${API}/exchanges/keys`, o),
        axios.get(`${API}/exchanges`, o)
      ]);
      setExchanges(exchangesRes.data.keys);
      setSupportedExchanges(supportedRes.data.exchanges);
    } catch { }
    finally { setLoading(false); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/exchanges/keys`, formData, o);
      toast.success('Exchange conectado');
      setShowAddDialog(false);
      loadData();
      setFormData({ exchange_id: '', api_key: '', api_secret: '', api_passphrase: '', testnet: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al conectar exchange');
    }
  };

  const handleDelete = async (keyId) => {
    if (!window.confirm('¿Desconectar este exchange?')) return;
    try {
      await axios.delete(`${API}/exchanges/keys/${keyId}`, o);
      toast.success('Exchange desconectado');
      loadData();
    } catch { toast.error('Error al desconectar'); }
  };

  const handleChangeEmail = async () => {
    if (!newEmail.trim() || !emailPassword) {
      toast.error('Completa el nuevo correo y tu contraseña');
      return;
    }
    try {
      await axios.post(`${API}/auth/change-email?new_email=${encodeURIComponent(newEmail.trim())}&current_password=${encodeURIComponent(emailPassword)}`, {}, o);
      toast.success('Correo actualizado');
      setNewEmail('');
      setEmailPassword('');
      await checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar correo');
    }
  };

  const handleChangePassword = async () => {
    if (!currentPw || !newPw) {
      toast.error('Completa todos los campos');
      return;
    }
    if (newPw.length < 8) {
      toast.error('La nueva contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (newPw !== confirmPw) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    try {
      await axios.post(`${API}/auth/change-password?current_password=${encodeURIComponent(currentPw)}&new_password=${encodeURIComponent(newPw)}`, {}, o);
      toast.success('Contraseña actualizada');
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    }
  };

  if (loading) {
    return (<Layout><div className="flex items-center justify-center h-full"><div className="text-center"><div className="aegis-spinner mx-auto"></div><p className="text-[#A1A1AA] text-sm mt-3">Cargando...</p></div></div></Layout>);
  }

  const tabs = [
    { id: 'account', label: 'Cuenta' },
    { id: 'exchanges', label: 'Exchanges' },
  ];

  return (
    <Layout>
      <div className="space-y-6" data-testid="settings-page">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Configuración</h1>
          <p className="text-[#A1A1AA] mt-1">Gestiona tu cuenta y conexiones de exchange</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-[#1E1E1E] p-1 rounded">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded text-sm font-medium transition-all ${tab === t.id ? 'bg-[#00D4FF] text-black' : 'text-[#A1A1AA] hover:text-white hover:bg-[#27272A]'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ===== TAB: CUENTA ===== */}
        {tab === 'account' && (
          <div className="space-y-4">
            {/* Tarjeta de perfil arriba — ocupa todo el ancho */}
            <Card className="bg-[#121212] border-[#27272A]">
              <CardContent className="py-5">
                <div className="flex items-center gap-5">
                  <div className="w-16 h-16 rounded-full bg-[#00D4FF] flex items-center justify-center shrink-0">
                    <User size={28} className="text-black" />
                  </div>
                  <div className="flex-1 flex items-center gap-8 flex-wrap">
                    <div>
                      <p className="text-xs text-[#A1A1AA] uppercase tracking-wider">Nombre</p>
                      <p className="text-white font-bold text-lg">{user?.name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#A1A1AA] uppercase tracking-wider">Correo</p>
                      <p className="text-white mono">{user?.email}</p>
                    </div>
                    <div>
                      <p className="text-xs text-[#A1A1AA] uppercase tracking-wider">País</p>
                      <div className="flex items-center gap-1.5">
                        <Globe size={14} className="text-[#00D4FF]" />
                        <p className="text-white">{user?.country || 'No registrado'}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-[#A1A1AA] uppercase tracking-wider">Rol</p>
                      <span className={`inline-block mt-0.5 px-2 py-0.5 rounded text-xs font-bold ${user?.role === 'admin' ? 'bg-red-500/10 text-red-400' : 'bg-[#00D4FF]/10 text-[#00D4FF]'}`}>
                        {user?.role === 'admin' ? 'Administrador' : 'Usuario'}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Correo y contraseña lado a lado */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="bg-[#121212] border-[#27272A]">
                <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><Mail size={18} /> Cambiar correo electrónico</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label>Nuevo correo</Label>
                    <Input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="nuevo@correo.com" type="email" />
                  </div>
                  <div>
                    <Label>Contraseña actual (para confirmar)</Label>
                    <Input value={emailPassword} onChange={e => setEmailPassword(e.target.value)} type="password" placeholder="Tu contraseña" />
                  </div>
                  <Button onClick={handleChangeEmail} className="w-full bg-[#00D4FF] text-black">Actualizar correo</Button>
                  <p className="text-xs text-[#52525B]">El correo anterior será reemplazado. Usa el nuevo para iniciar sesión.</p>
                </CardContent>
              </Card>

              <Card className="bg-[#121212] border-[#27272A]">
                <CardHeader><CardTitle className="text-white text-base flex items-center gap-2"><Lock size={18} /> Cambiar contraseña</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label>Contraseña actual</Label>
                    <Input value={currentPw} onChange={e => setCurrentPw(e.target.value)} type="password" placeholder="Contraseña actual" />
                  </div>
                  <div>
                    <Label>Nueva contraseña</Label>
                    <Input value={newPw} onChange={e => setNewPw(e.target.value)} type="password" placeholder="Mínimo 8 caracteres" />
                  </div>
                  <div>
                    <Label>Confirmar nueva contraseña</Label>
                    <Input value={confirmPw} onChange={e => setConfirmPw(e.target.value)} type="password" placeholder="Repite la nueva contraseña" />
                  </div>
                  <Button onClick={handleChangePassword} className="w-full bg-[#00D4FF] text-black">Actualizar contraseña</Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* ===== TAB: EXCHANGES ===== */}
        {tab === 'exchanges' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">Exchanges Conectados</h2>
                <p className="text-sm text-[#A1A1AA]">Tus claves API se guardan cifradas</p>
              </div>
              <Button onClick={() => setShowAddDialog(true)} className="bg-[#00D4FF] text-black font-bold uppercase tracking-wider text-xs px-6 py-3 rounded-sm">
                <Plus size={16} className="mr-2" /> Agregar Exchange
              </Button>
            </div>

            {exchanges.length === 0 ? (
              <Card className="bg-[#121212] border-[#27272A]">
                <CardContent className="py-12 text-center">
                  <Key className="mx-auto text-[#A1A1AA] mb-3" size={40} />
                  <p className="text-[#A1A1AA] mb-4">Sin exchanges conectados</p>
                  <Button onClick={() => setShowAddDialog(true)} className="bg-[#00D4FF] text-black">Conecta tu primer Exchange</Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {exchanges.map(ex => (
                  <Card key={ex.key_id} className="bg-[#121212] border-[#27272A]">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-lg font-bold text-white capitalize">{ex.exchange_id}</h3>
                          <p className="text-xs text-[#A1A1AA] mono">API Key: {ex.api_key.substring(0, 8)}***</p>
                        </div>
                        {ex.is_connected ? <CheckCircle className="text-positive" size={20} /> : <XCircle className="text-negative" size={20} />}
                      </div>
                      <div className="flex justify-between items-center">
                        <span className={`text-xs px-2 py-1 rounded ${ex.is_connected ? 'bg-[#00FF88]/10 text-[#00FF88]' : 'bg-red-500/10 text-red-500'}`}>
                          {ex.is_connected ? 'Conectado' : 'Desconectado'}
                        </span>
                        <Button onClick={() => handleDelete(ex.key_id)} variant="outline" size="sm" className="text-red-500 border-red-500/20 hover:bg-red-500/10">
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Dialog agregar exchange */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-md">
            <DialogHeader>
              <DialogTitle>Conectar Exchange</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAdd} className="space-y-4">
              <div>
                <Label>Exchange</Label>
                <Select value={formData.exchange_id} onValueChange={v => setFormData({...formData, exchange_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Selecciona exchange" /></SelectTrigger>
                  <SelectContent>
                    {supportedExchanges.map(ex => <SelectItem key={ex} value={ex}>{ex}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>API Key</Label>
                <Input value={formData.api_key} onChange={e => setFormData({...formData, api_key: e.target.value})} required placeholder="Tu API Key" />
              </div>
              <div>
                <Label>API Secret</Label>
                <Input type="password" value={formData.api_secret} onChange={e => setFormData({...formData, api_secret: e.target.value})} required placeholder="Tu API Secret" />
              </div>
              <div>
                <Label>Passphrase (opcional)</Label>
                <Input value={formData.api_passphrase} onChange={e => setFormData({...formData, api_passphrase: e.target.value})} placeholder="Solo algunos exchanges lo requieren" />
              </div>
              <Button type="submit" className="w-full bg-[#00D4FF] text-black">Conectar Exchange</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Settings;
